import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import SyncIcon from "@mui/icons-material/Sync";

import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import type {
  ConfigTemplate,
  ConfigTemplateSyncResponse,
} from "../types";
import { useToast } from "../toast";
import ConfirmDialog from "../dialogs/ConfirmDialog";
import ConfigTemplateDialog from "../dialogs/ConfigTemplateDialog";

function configKeyCount(values: ConfigTemplate["config_values"]): number {
  return Object.keys(values ?? {}).length;
}

export default function ConfigTemplates() {
  const api = useAuthApi();
  const toast = useToast();
  const [items, setItems] = useState<ConfigTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ConfigTemplate | null>(null);
  const [deleting, setDeleting] = useState<ConfigTemplate | null>(null);
  const [syncingId, setSyncingId] = useState<number | null>(null);
  const [lastSyncResult, setLastSyncResult] = useState<ConfigTemplateSyncResponse | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const templates = await api<ConfigTemplate[]>(`${MGMT}/config-templates`);
      setItems(templates ?? []);
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await api(`${MGMT}/config-templates/${deleting.id}`, { method: "DELETE" });
      toast.show("Template deleted");
      setDeleting(null);
      load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  };

  const handleSync = async (template: ConfigTemplate) => {
    setSyncingId(template.id);
    try {
      const result = await api<ConfigTemplateSyncResponse>(
        `${MGMT}/config-templates/${template.id}/sync`,
        { method: "POST" }
      );
      if (result) {
        setLastSyncResult(result);
        toast.show(
          `Sync done: ${result.in_sync} in sync, ${result.out_of_sync} out of sync, ${result.queued_changes} changes queued`
        );
      } else {
        setLastSyncResult(null);
      }
      load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSyncingId(null);
    }
  };

  return (
    <>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
          Add Template
        </Button>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      {lastSyncResult && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Last Sync Result (Template #{lastSyncResult.template_id})
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total: {lastSyncResult.total_chargers} • In sync: {lastSyncResult.in_sync} • Out of sync:{" "}
            {lastSyncResult.out_of_sync} • Queued changes: {lastSyncResult.queued_changes}
          </Typography>
        </Paper>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Config Keys</TableCell>
              <TableCell align="right">Charger Assignments</TableCell>
              <TableCell align="right">Group Assignments</TableCell>
              <TableCell align="right">Model Assignments</TableCell>
              <TableCell align="right">Assigned Chargers</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Box sx={{ py: 3 }}>
                    <Typography color="text.secondary">
                      No templates yet. Create one and assign it by chargers, groups, or models.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {items.map((t) => {
              return (
                <TableRow key={t.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{t.name}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>{t.description || "—"}</TableCell>
                  <TableCell align="right">{configKeyCount(t.config_values)}</TableCell>
                  <TableCell align="right">{t.charger_config_ids.length}</TableCell>
                  <TableCell align="right">{t.charger_group_ids.length}</TableCell>
                  <TableCell align="right">{t.charger_model_ids.length}</TableCell>
                  <TableCell align="right">
                    <Chip size="small" label={t.resolved_charger_count} />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      title="Sync assigned chargers"
                      onClick={() => handleSync(t)}
                      disabled={syncingId === t.id}
                    >
                      <SyncIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => setEditing(t)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => setDeleting(t)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfigTemplateDialog
        open={creating || editing !== null}
        template={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSaved={() => {
          setCreating(false);
          setEditing(null);
          load();
        }}
      />

      <ConfirmDialog
        open={deleting !== null}
        title="Delete config template?"
        message={`Delete "${deleting?.name}"? Chargers can stay linked with a null template, but this template will be removed.`}
        onCancel={() => setDeleting(null)}
        onConfirm={handleDelete}
      />
    </>
  );
}
